import { BigNumber, _ } from "@sablier/v2-mixins";
import { Token } from "@sablier/v2-models";
import { vendors } from "@sablier/v2-utils";
import { assign, createMachine } from "xstate";
import type { IProvider, IToken } from "@sablier/v2-types";

type MachineContext = {
  balance: string | undefined;
  error: string | undefined;
};

type Payload = {
  provider: IProvider;
  owner: string;
  token: IToken;
};

type MachineServices = {
  doCheck: { data: string };
};

type MachineEvents =
  | {
      type: "CHECK";
      payload: Payload;
    }
  | {
      type: "RESOLVE";
      payload: Payload;
    }
  | { type: "FAIL" }
  | { type: "RESET" };

const initialContext: MachineContext = {
  balance: undefined,
  error: undefined,
};

function create({ id }: { id: string }) {
  return createMachine(
    {
      tsTypes: {} as import("./balance.typegen").Typegen0,
      id: `balanceMachine-${id}`,
      initial: "idle",
      context: initialContext,
      schema: {
        context: {} as MachineContext,
        events: {} as MachineEvents,
        services: {} as MachineServices,
      },
      predictableActionArguments: true,
      states: {
        idle: {
          entry: "doContextClean",
          on: {
            CHECK: [
              {
                cond: "isPreparedForChecks",
                target: "checking",
              },
              {
                target: "resolved",
              },
            ],
            FAIL: {
              target: "failed",
            },
          },
        },
        checking: {
          invoke: {
            id: "check",
            src: "doCheck",
            onDone: {
              actions: "doAssignForResolution",
              target: "resolved",
            },
            onError: {
              actions: "doError",
              target: "failed",
            },
          },
          on: {
            RESET: {
              target: "idle",
            },
            FAIL: {
              target: "failed",
            },
          },
        },
        resolved: {
          on: {
            RESET: {
              target: "idle",
            },
            FAIL: {
              target: "failed",
            },
          },
        },
        failed: {
          on: {
            RESET: {
              target: "idle",
            },
            FAIL: {
              target: "failed",
            },
          },
        },
      },
    },

    {
      actions: {
        doAssignForResolution: assign({
          balance: (_context, event) => {
            try {
              if (event.data) {
                const support = new BigNumber(event.data);
                if (support.isFinite()) {
                  return support.toString();
                }
              }
            } catch (error) {
              vendors.crash.log(error);
            }
            return undefined;
          },
        }),

        doError: assign({
          error: (_context, event) => _.toString(_.get(event, "data.message")),
        }),
        doContextClean: assign({
          balance: () => initialContext.balance,
          error: () => initialContext.error,
        }),
      },
      guards: {
        isPreparedForChecks: (_context, event): boolean => {
          const { owner, provider, token } = event.payload;
          return !(
            _.isNil(provider) ||
            _.isNil(token) ||
            _.isNilOrEmptyString(owner) ||
            _.isNilOrEmptyString(token.address)
          );
        },
      },
      services: {
        doCheck: async (_context, event): Promise<string> => {
          const { owner, provider, token } = event.payload;

          const balance = await Token.getBalanceFor({
            address: token.address,
            owner,
            provider,
          });

          return balance.toString();
        },
      },
    },
  );
}

const balance = {
  create,
};

export default balance;
